import React, { useMemo, useCallback } from 'react';
import { Flex } from '@withjoy/joykit';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { AccommodationsTileV2, AccommodationsTileV2Skeleton } from './AccommodationsV2/AccommodationsTileV2';
import { AccommodationCombined, AccommodationSourceType } from './Accommodations/Accommodation.types';
import { ExploreOtherOptions } from './ExploreOtherOptions';
import { RedirectWithSafeLink } from '@apps/guest/components/RedirectWithSafeLink';
import { GuestTravelMapComponentFragment, AccommodationPropertyPhotosQuery } from '@graphql/generated';
import { EventVenue } from '@shared/utils/eventPlace/EventPlace.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import {
  ACCOMMODATIONS_GRID_FULL_WIDTH_HORIZONTAL,
  ACCOMMODATIONS_GRID_FULL_WIDTH_STANDARD,
  ACCOMMODATIONS_GRID_FULL_WIDTH_WITH_FEW_HOTELS_STANDARD_TILES,
  ACCOMMODATIONS_GRID_STANDARD,
  ACCOMMODATIONS_GRID_STANDARD_HORIZONTAL,
  ACCOMMODATIONS_GRID_STANDARD_WITH_FEW_HOTELS,
  FULL_WIDTH_CONTAINER_MARGIN,
  LAYOUT_DIMENSIONS,
  MAP_GRID_FULL_WIDTH_STANDARD,
  MAP_GRID_FULL_WIDTH_WITH_FEW_HOTELS,
  MAP_GRID_FULL_WIDTH_WITH_FEW_HOTELS_STANDARD_TILES,
  MAP_GRID_STANDARD,
  MAP_GRID_STANDARD_WITH_FEW_HOTELS,
  SPACING,
} from '../TravelMapExpandedLayout.constants';
interface TravelMapExpandedLayoutProps
  extends Readonly<{
    loading: boolean;
    renderMap: React.ReactNode;
    primaryAccommodation: AccommodationCombined | null;
    nonPrimaryAccommodations: AccommodationCombined[];
    destination: EventVenue;
    isInterceptEnabled: boolean;
    getReservationsCountData: (joyPlaceId?: string | null | undefined) => number | null | undefined;
    accommodationPropertyPhotos: AccommodationPropertyPhotosQuery | undefined;
    showDefaultData: boolean;
    setLocationBeingHovered: (location: string | undefined) => void;
    onAccommodationsConsoleOpen: () => void;
    isAccommodationLinkInterceptionEnabled: boolean;
    travelMapComponentData: GuestTravelMapComponentFragment;
    sortedHotelList: AccommodationCombined[];
    pageSlug: string;
    eventTimezone: string | null | undefined;
    safeLink: string | undefined;
    setSafeLink: (link: string | undefined) => void;
    isFullWidth: boolean;
    enableHoverShadow: boolean;
    eventPhoto: string | null | undefined;
  }> {}

export const TravelMapExpandedLayout: React.FC<TravelMapExpandedLayoutProps> = ({
  loading,
  renderMap,
  primaryAccommodation,
  nonPrimaryAccommodations,
  destination,
  isInterceptEnabled,
  getReservationsCountData,
  accommodationPropertyPhotos,
  showDefaultData,
  setLocationBeingHovered,
  onAccommodationsConsoleOpen,
  isAccommodationLinkInterceptionEnabled,
  travelMapComponentData,
  sortedHotelList,
  pageSlug,
  eventTimezone,
  safeLink,
  setSafeLink,
  isFullWidth,
  enableHoverShadow,
  eventPhoto,
}) => {
  // ============================================================================
  // RESPONSIVE BREAKPOINT DETECTION
  // ============================================================================
  const [isDesktop] = useResponsive({ values: { _: false, md3: true } });

  // ============================================================================
  // ACCOMMODATIONS DATA PREPARATION
  // ============================================================================
  const accommodationsList = useMemo(() => {
    if (!primaryAccommodation) return nonPrimaryAccommodations;
    return [primaryAccommodation, ...nonPrimaryAccommodations];
  }, [primaryAccommodation, nonPrimaryAccommodations]);

  // ============================================================================
  // LAYOUT MODE DETERMINATION
  // ============================================================================
  const layoutForLessThan4Hotels = useMemo(() => {
    return accommodationsList.length < 4;
  }, [accommodationsList.length]);

  /**
   * Horizontal view is enabled when:
   * - Less than 4 accommodations AND desktop AND full-width, OR
   * - Only 1 accommodation (single hotel view)
   */
  const enableHorizontalView = useMemo(() => {
    return (accommodationsList.length < 4 && isDesktop && isFullWidth) || accommodationsList.length === 1;
  }, [accommodationsList.length, isDesktop, isFullWidth]);

  /**
   * Single hotel view is enabled when there's exactly 1 accommodation
   * This affects layout, alignment, and grid behavior
   */
  const enableSingleHotelView = useMemo(() => {
    return accommodationsList.length === 1;
  }, [accommodationsList.length]);

  // Compute frequently used style props via useMemo to keep JSX readable and props stable.
  // NOTE: Do not change any resulting values; this preserves exact current UI/behavior.
  const containerWidth = useMemo(() => {
    const singleHotelWidth = pxToRem(LAYOUT_DIMENSIONS.SINGLE_HOTEL_CONTAINER_WIDTH);
    const fullWidthMinusMargins = `calc(100vw - ${pxToRem(2 * FULL_WIDTH_CONTAINER_MARGIN)})`;

    if (isFullWidth) {
      // Full width: constrain inner container or allow edge-to-edge minus margins
      return {
        _: enableHorizontalView ? singleHotelWidth : fullWidthMinusMargins,
        md: enableSingleHotelView ? singleHotelWidth : fullWidthMinusMargins,
      } as const;
    }
    // Non-full width: single hotel is fixed width; otherwise inherit
    return enableSingleHotelView ? singleHotelWidth : '';
  }, [isFullWidth, enableHorizontalView, enableSingleHotelView]);

  const outerAlignItems = useMemo(() => {
    // Single hotel view always centers content
    if (enableSingleHotelView) {
      return { alignItems: 'center' as const };
    }
    // Otherwise, center on mobile when horizontal, initial on desktop
    return { alignItems: { _: enableHorizontalView ? 'center' : 'initial', md: 'initial' } as const };
  }, [enableSingleHotelView, enableHorizontalView]);

  const innerDisplay = useMemo(() => {
    // Single hotel view always uses flex layout
    if (enableSingleHotelView) {
      return 'flex';
    }
    // Otherwise, flex on mobile when horizontal, grid on desktop
    return { _: enableHorizontalView ? 'flex' : 'grid', md: 'grid' } as const;
  }, [enableSingleHotelView, enableHorizontalView]);

  const innerFlexDirection = useMemo(() => {
    // Single hotel view always stacks vertically
    if (enableSingleHotelView) {
      return { flexDirection: 'column' as const };
    }
    // Otherwise, column on mobile when horizontal, row on desktop
    return { flexDirection: { _: enableHorizontalView ? 'column' : 'row', md: 'row' } as const };
  }, [enableSingleHotelView, enableHorizontalView]);

  /**
   * Determines the grid template columns for the main layout based on width mode and accommodation count.
   * This controls the space allocation between the map and accommodations list.
   */
  const getMainGridTemplateColumns = (fullWidth: boolean, horizontalView: boolean, lessThan4Hotels: boolean) => {
    if (fullWidth) {
      if (horizontalView) {
        return MAP_GRID_FULL_WIDTH_WITH_FEW_HOTELS;
      }
      if (lessThan4Hotels) {
        return MAP_GRID_FULL_WIDTH_WITH_FEW_HOTELS_STANDARD_TILES;
      }
      return MAP_GRID_FULL_WIDTH_STANDARD;
    }

    if (lessThan4Hotels) {
      return MAP_GRID_STANDARD_WITH_FEW_HOTELS;
    }
    return MAP_GRID_STANDARD;
  };

  const mainGridTemplateColumns = useMemo(() => {
    return getMainGridTemplateColumns(isFullWidth, enableHorizontalView, layoutForLessThan4Hotels);
  }, [isFullWidth, enableHorizontalView, layoutForLessThan4Hotels]);

  const mainGap = useMemo(() => {
    // Full-width layouts use responsive gaps, single hotel uses large gap, others use small
    return isFullWidth
      ? ({ sm: pxToRem(SPACING.ELEMENT_GAP_LARGE), md3: pxToRem(SPACING.ELEMENT_GAP_EXTRA_LARGE) } as const)
      : enableSingleHotelView
      ? pxToRem(SPACING.ELEMENT_GAP_LARGE)
      : pxToRem(SPACING.ELEMENT_GAP_SMALL);
  }, [isFullWidth, enableSingleHotelView]);

  const mapHeight = useMemo(() => {
    // Horizontal view uses full height, others use viewport height minus offset
    return enableHorizontalView ? '100%' : `calc(100vh - ${pxToRem(SPACING.MAP_VIEWPORT_HEIGHT_OFFSET)})`;
  }, [enableHorizontalView]);

  const mapPositionProps = useMemo(() => {
    // Sticky map on grid layouts; initial for horizontal/single-hotel views
    return enableSingleHotelView ? {} : ({ position: { _: enableHorizontalView ? 'initial' : 'sticky', md: 'sticky' } as const } as const);
  }, [enableSingleHotelView, enableHorizontalView]);

  const listHeightProps = useMemo(() => {
    return !enableSingleHotelView ? ({ height: { _: enableHorizontalView ? 'initial' : '100%', md: '100%' } as const } as const) : {};
  }, [enableSingleHotelView, enableHorizontalView]);

  const fullWidthListColumns = useMemo(() => {
    // Columns for the accommodations list when container is full width
    return enableHorizontalView
      ? ACCOMMODATIONS_GRID_FULL_WIDTH_HORIZONTAL
      : layoutForLessThan4Hotels
      ? ACCOMMODATIONS_GRID_FULL_WIDTH_WITH_FEW_HOTELS_STANDARD_TILES
      : ACCOMMODATIONS_GRID_FULL_WIDTH_STANDARD;
  }, [enableHorizontalView, layoutForLessThan4Hotels]);

  const regularListColumns = useMemo(() => {
    // Columns for the accommodations list when container is normal width
    return enableHorizontalView ? ACCOMMODATIONS_GRID_STANDARD_HORIZONTAL : layoutForLessThan4Hotels ? ACCOMMODATIONS_GRID_STANDARD_WITH_FEW_HOTELS : ACCOMMODATIONS_GRID_STANDARD;
  }, [enableHorizontalView, layoutForLessThan4Hotels]);

  // Create a Map for O(1) photo lookups by joyPlaceId
  const accommodationPhotosMap = useMemo(() => {
    const photosMap = new Map<string, ReadonlyArray<{ url: string }>>();
    accommodationPropertyPhotos?.searchAccommodationProperties?.properties?.forEach(property => {
      if (property.joyPlaceId && property.photos) {
        photosMap.set(property.joyPlaceId, property.photos);
      }
    });
    return photosMap;
  }, [accommodationPropertyPhotos]);

  // Helper function to render regular accommodations
  const renderAccommodations = useCallback(() => {
    if (!accommodationsList || accommodationsList.length === 0) return null;

    return accommodationsList.map((accommodation, idx) => {
      const photosFromMap = accommodation?.joyPlaceId ? accommodationPhotosMap.get(accommodation.joyPlaceId) : undefined;
      // Convert readonly array to mutable array for component prop
      const accommodationPhotos = photosFromMap ? [...photosFromMap] : undefined;

      return (
        <Flex
          key={`${accommodation?.id}-${idx}`}
          width="100%"
          height="100%"
          minHeight={0}
        >
          <AccommodationsTileV2
            accommodation={accommodation as AccommodationCombined}
            reservationCount={getReservationsCountData(accommodation?.joyPlaceId) ?? 0}
            idx={idx}
            enableHorizontalView={enableHorizontalView}
            locationInfo={destination}
            eventPhoto={eventPhoto}
            accommodationPropertyPhotos={accommodationPhotos}
            enableHoverShadow={enableHoverShadow}
            showDefaultData={showDefaultData}
            isInterceptEnabled={isInterceptEnabled}
            setLocationBeingHovered={setLocationBeingHovered}
            from={AccommodationSourceType.AccomPageGrid}
            onAccommodationsConsoleOpen={onAccommodationsConsoleOpen}
            isAccommodationLinkInterceptionEnabled={isAccommodationLinkInterceptionEnabled}
          />
        </Flex>
      );
    });
  }, [
    accommodationsList,
    accommodationPhotosMap,
    getReservationsCountData,
    enableHorizontalView,
    destination,
    eventPhoto,
    enableHoverShadow,
    showDefaultData,
    isInterceptEnabled,
    setLocationBeingHovered,
    onAccommodationsConsoleOpen,
    isAccommodationLinkInterceptionEnabled,
  ]);

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      position={'relative'}
      width={isFullWidth ? '100vw' : '100%'}
      left={isFullWidth ? 'calc(-50vw + 50%)' : 'auto'} // Full-width positioning trick
      gap={7}
    >
      {/* Main container with responsive alignment */}
      <Flex
        width={isFullWidth ? '100vw' : '100%'}
        flexDirection="column"
        {...outerAlignItems}
      >
        {/* Map and accommodations grid container */}
        <Flex
          width={containerWidth}
          marginX={isFullWidth ? pxToRem(FULL_WIDTH_CONTAINER_MARGIN) : pxToRem(SPACING.CONTAINER_PADDING_DEFAULT)}
          display={innerDisplay}
          {...innerFlexDirection}
          gridTemplateColumns={mainGridTemplateColumns}
          gap={mainGap}
        >
          {/* Map container with responsive positioning */}
          <Flex
            width="100%"
            height={mapHeight}
            minHeight={pxToRem(SPACING.MAP_MINIMUM_HEIGHT)}
            zIndex={2}
            {...mapPositionProps}
            top={pxToRem(SPACING.MAP_STICKY_TOP_OFFSET)}
          >
            {renderMap}
          </Flex>

          {/* Accommodations list container */}
          <Flex
            width="100%"
            {...listHeightProps}
            flexDirection="column"
            gap={pxToRem(SPACING.ELEMENT_GAP_MEDIUM)}
            zIndex={1}
          >
            {isFullWidth ? (
              /* Full Width Layout: Responsive grid with 1-3 columns based on screen size */
              <SkeletonGroup
                isReady={!loading}
                placeholder={<AccommodationsTileV2Skeleton />}
                {...(!enableSingleHotelView
                  ? {
                      display: 'grid',
                      gridTemplateColumns: fullWidthListColumns,
                      gridAutoRows: '1fr',
                    }
                  : {})}
                gap={pxToRem(SPACING.ELEMENT_GAP_LARGE)}
              >
                {renderAccommodations()}
              </SkeletonGroup>
            ) : (
              /* Normal Width Layout: 1-2 columns based on accommodation count */
              <SkeletonGroup
                isReady={!loading}
                placeholder={<AccommodationsTileV2Skeleton />}
                {...(!enableSingleHotelView
                  ? {
                      display: 'grid',
                      gridTemplateColumns: regularListColumns,
                      gridAutoRows: '1fr',
                    }
                  : {})}
                gap={pxToRem(SPACING.ELEMENT_GAP_SMALL)}
              >
                {renderAccommodations()}
              </SkeletonGroup>
            )}
          </Flex>
        </Flex>
      </Flex>

      {/* Explore Other Options - Centered at bottom */}
      {!travelMapComponentData.hideExploreCTA && (
        <ExploreOtherOptions
          accommodations={sortedHotelList}
          venue={destination || null}
          pageSlug={pageSlug}
          eventTimezone={eventTimezone}
        />
      )}
      <RedirectWithSafeLink
        href={safeLink}
        skipTelemetry
        onClose={() => {
          setSafeLink(undefined);
        }}
      />
    </Flex>
  );
};
